// =====================================================================
// TOUTES LES TRADUCTIONS DE L'APPLICATION SONT DANS CE SEUL FICHIER.
// ALL OF THE APP'S TRANSLATIONS LIVE IN THIS ONE FILE.
//
// Vous pouvez corriger n'importe quel mot ici sans toucher au reste du
// code. La colonne "fr" est la reference : le francais est la langue par
// defaut et la langue de tout ce qui est imprime ou signe.
//
// CE QUI NE DOIT JAMAIS ETRE TRADUIT NI FIGURER ICI :
//   - les noms des unites (ils viennent de la base, tels qu'ecrits sur
//     le tableau papier)
//   - les noms des personnes
//   - les noms des banques : BHS, SGBS
//   - les references de compte et les IBAN
//
// Regle : si un mot designe une chose reelle qui porte un nom, il vient
// de la base de donnees, pas de ce fichier.
// =====================================================================

export type Lang = 'fr' | 'en'

export const strings = {
  // -------------------------------------------------------------------
  // Identite de l'application
  // -------------------------------------------------------------------
  // SEGISEN est le nom de la societe. Immeuble Escale est le nom du
  // batiment qu'elle gere. Ni l'un ni l'autre ne se traduit, et la
  // raison sociale reste en francais dans les deux langues : c'est une
  // denomination legale, pas une phrase.
  app_name: {
    fr: 'SÉGISEN',
    en: 'SÉGISEN',
  },
  app_legal_name: {
    fr: 'Société d’Exploitation et de Gestion Immobilière Sénégalaise',
    en: 'Société d’Exploitation et de Gestion Immobilière Sénégalaise',
  },
  app_subtitle: {
    fr: 'Immeuble Escale · Mbour',
    en: 'Immeuble Escale · Mbour',
  },
  app_tagline: {
    fr: 'Gestion locative',
    en: 'Rental management',
  },

  // -------------------------------------------------------------------
  // Connexion
  // -------------------------------------------------------------------
  login_title: {
    fr: 'Connexion',
    en: 'Sign in',
  },
  login_intro: {
    fr: 'Entrez votre adresse email. Vous recevrez un lien pour entrer, sans mot de passe.',
    en: 'Enter your email address. You will receive a link to sign in, with no password.',
  },
  login_email_label: {
    fr: 'Adresse email',
    en: 'Email address',
  },
  login_send: {
    fr: 'Recevoir mon lien',
    en: 'Send my link',
  },
  login_sending: {
    fr: 'Envoi en cours...',
    en: 'Sending...',
  },
  login_sent_title: {
    fr: 'Regardez vos emails',
    en: 'Check your email',
  },
  login_sent_body: {
    fr: 'Nous avons envoye un lien a {email}. Ouvrez-le sur ce telephone pour entrer dans l’application. Le lien est valable une heure.',
    en: 'We sent a link to {email}. Open it on this phone to enter the app. The link is valid for one hour.',
  },
  login_sent_again: {
    fr: 'Envoyer un nouveau lien',
    en: 'Send another link',
  },
  login_error_email: {
    fr: 'Cette adresse email n’est pas valide.',
    en: 'That email address is not valid.',
  },
  login_error_generic: {
    fr: 'L’envoi a echoue. Verifiez votre connexion et reessayez.',
    en: 'Sending failed. Check your connection and try again.',
  },
  // Le compte n'existe pas. L'application ne cree jamais de compte
  // toute seule : les trois comptes sont crees par le proprietaire.
  login_error_no_account: {
    fr: 'Aucun compte n’existe pour cette adresse. Seul le proprietaire peut creer un compte.',
    en: 'No account exists for this address. Only the owner can create an account.',
  },
  // Supabase limite le nombre d'emails par heure.
  login_error_rate_limit: {
    fr: 'Trop de demandes en peu de temps. Attendez quelques minutes avant de redemander un lien.',
    en: 'Too many requests in a short time. Wait a few minutes before asking for another link.',
  },
  // L'adresse du site n'est pas autorisee dans Supabase.
  login_error_redirect: {
    fr: 'L’adresse de ce site n’est pas autorisee dans Supabase. Le lien ne pourrait pas revenir ici.',
    en: 'This site’s address is not allowed in Supabase. The link could not come back here.',
  },
  // Le serveur d'envoi d'emails de Supabase a refuse.
  login_error_mail: {
    fr: 'Le serveur d’emails a refuse l’envoi. Ce n’est pas votre connexion.',
    en: 'The email server refused to send. This is not your connection.',
  },
  // Detail technique, replie sous le message clair. Il ne sert pas a
  // l'utilisateur mais evite de devoir deviner en cas de panne.
  login_error_detail: {
    fr: 'Detail technique',
    en: 'Technical detail',
  },

  // -------------------------------------------------------------------
  // Panneau de diagnostic de la configuration
  // -------------------------------------------------------------------
  diag_title: {
    fr: 'Verifier la configuration',
    en: 'Check configuration',
  },
  diag_url: {
    fr: 'Adresse utilisee',
    en: 'Address in use',
  },
  diag_key: {
    fr: 'Cle publique',
    en: 'Public key',
  },
  diag_key_length: {
    fr: '{n} caracteres',
    en: '{n} characters',
  },
  diag_project_key: {
    fr: 'Projet de la cle',
    en: 'Key project',
  },
  diag_project_url: {
    fr: 'Projet de l’adresse',
    en: 'Address project',
  },
  diag_match_ok: {
    fr: 'La cle correspond bien a ce projet.',
    en: 'The key belongs to this project.',
  },
  diag_match_bad: {
    fr: 'La cle n’appartient pas a ce projet. C’est la cause d’une erreur 401 « Invalid API key ». Corrigez VITE_SUPABASE_URL ou VITE_SUPABASE_ANON_KEY dans Netlify, puis redeployez.',
    en: 'The key does not belong to this project. This causes a 401 "Invalid API key". Fix VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY in Netlify, then redeploy.',
  },
  diag_key_cleaned: {
    fr: 'La cle contenait des caracteres invalides qui ont ete retires. Recollez-la proprement dans Netlify.',
    en: 'The key contained invalid characters that were removed. Re-paste it cleanly in Netlify.',
  },
  diag_url_corrected: {
    fr: 'L’adresse contenait un chemin en trop, retire automatiquement.',
    en: 'The address contained an extra path, removed automatically.',
  },
  logout: {
    fr: 'Se deconnecter',
    en: 'Sign out',
  },

  // -------------------------------------------------------------------
  // Acces refuse : compte cree mais sans role attribue
  // -------------------------------------------------------------------
  no_role_title: {
    fr: 'Acces non encore autorise',
    en: 'Access not yet granted',
  },
  no_role_body: {
    fr: 'Votre compte existe mais aucun role ne lui a encore ete attribue. Le proprietaire doit vous autoriser avant que vous puissiez voir les donnees.',
    en: 'Your account exists but no role has been assigned to it yet. The owner must authorise you before you can see any data.',
  },

  // -------------------------------------------------------------------
  // Navigation du bas
  // -------------------------------------------------------------------
  nav_home: {
    fr: 'Accueil',
    en: 'Home',
  },
  nav_rent: {
    fr: 'Loyers',
    en: 'Rent',
  },
  nav_expenses: {
    fr: 'Depenses',
    en: 'Expenses',
  },
  nav_building: {
    fr: 'Immeuble',
    en: 'Building',
  },
  nav_more: {
    fr: 'Plus',
    en: 'More',
  },

  // -------------------------------------------------------------------
  // Unites
  // -------------------------------------------------------------------
  units_title: {
    fr: 'Les unites',
    en: 'Units',
  },
  units_count: {
    fr: '{n} unites',
    en: '{n} units',
  },
  unit_kind_commercial: {
    fr: 'Magasin',
    en: 'Shop',
  },
  unit_kind_habitation: {
    fr: 'Habitation',
    en: 'Residential',
  },
  unit_occupied: {
    fr: 'Occupe',
    en: 'Occupied',
  },

  // Duree d'occupation. Le francais accorde « an » au pluriel, d'ou
  // des cles distinctes plutot qu'un « s » ajoute au hasard.
  occupancy_1_year: { fr: 'Depuis 1 an', en: 'For 1 year' },
  occupancy_years: { fr: 'Depuis {n} ans', en: 'For {n} years' },
  occupancy_1_year_months: {
    fr: 'Depuis 1 an et {m} mois',
    en: 'For 1 year and {m} months',
  },
  occupancy_years_months: {
    fr: 'Depuis {n} ans et {m} mois',
    en: 'For {n} years and {m} months',
  },
  occupancy_months: { fr: 'Depuis {m} mois', en: 'For {m} months' },
  occupancy_1_month: { fr: 'Depuis 1 mois', en: 'For 1 month' },
  occupancy_new: { fr: 'Entre ce mois-ci', en: 'Moved in this month' },
  occupancy_unknown: {
    fr: 'Duree inconnue, aucune date de debut',
    en: 'Duration unknown, no start date',
  },
  unit_vacant: {
    fr: 'Vide',
    en: 'Vacant',
  },
  unit_vacant_since: {
    fr: 'Vide depuis le {date}',
    en: 'Vacant since {date}',
  },
  units_empty_title: {
    fr: 'Aucune unite enregistree',
    en: 'No units recorded',
  },
  units_empty_body: {
    fr: 'Les neuf unites de l’immeuble n’ont pas encore ete saisies dans la base. Elles doivent porter exactement les noms du tableau papier.',
    en: 'The building’s nine units have not been entered into the database yet. They must carry exactly the names used on the paper sheet.',
  },

  // -------------------------------------------------------------------
  // Ecrans a venir
  // -------------------------------------------------------------------
  coming_soon_title: {
    fr: 'Bientot disponible',
    en: 'Coming soon',
  },
  coming_soon_body: {
    fr: 'Cet ecran sera construit dans une prochaine etape.',
    en: 'This screen will be built in a later step.',
  },

  // -------------------------------------------------------------------
  // Etats communs
  // -------------------------------------------------------------------
  loading: {
    fr: 'Chargement...',
    en: 'Loading...',
  },
  retry: {
    fr: 'Reessayer',
    en: 'Try again',
  },
  error_load: {
    fr: 'Impossible de charger les donnees.',
    en: 'Could not load the data.',
  },
  offline_banner: {
    fr: 'Pas de connexion. Rien n’est perdu : ce que vous avez tape reste a l’ecran.',
    en: 'No connection. Nothing is lost: what you typed stays on screen.',
  },
  offline_short: {
    fr: 'Hors connexion',
    en: 'Offline',
  },

  // -------------------------------------------------------------------
  // Langue et roles
  // -------------------------------------------------------------------
  language: {
    fr: 'Langue',
    en: 'Language',
  },
  role_proprietaire: {
    fr: 'Proprietaire',
    en: 'Owner',
  },
  role_gerant: {
    fr: 'Gerant',
    en: 'Manager',
  },
  role_assistant: {
    fr: 'Assistant',
    en: 'Assistant',
  },

  // -------------------------------------------------------------------
  // Choix de l'annee : le point d'entree de l'application
  // -------------------------------------------------------------------
  year_choose: { fr: 'Choisissez une annee', en: 'Choose a year' },
  year_choose_body: {
    fr: 'Tout ce que vous verrez ensuite se rapporte a cette annee : loyers, depenses, pieces justificatives et rapports. Vous pourrez en changer a tout moment, en haut de l’ecran.',
    en: 'Everything you see next relates to that year: rent, expenses, documents and reports. You can change it at any time, at the top of the screen.',
  },
  year_current: { fr: 'Annee en cours', en: 'Current year' },
  year_has_rent: { fr: '{n} loyers enregistres', en: '{n} rents recorded' },
  year_has_leases: { fr: '{n} baux, aucun loyer encore', en: '{n} leases, no rent yet' },
  year_empty: { fr: 'Annee vide', en: 'Empty year' },
  year_change: { fr: 'Changer d’annee', en: 'Change year' },

  home_year_scope: {
    fr: 'Tout ce qui suit porte sur {year}, du 05/01/{year} au 05/01 de l’annee suivante.',
    en: 'Everything below covers {year}, from 05/01/{year} to 05/01 of the following year.',
  },
  home_year_empty: { fr: 'Aucun loyer enregistre', en: 'No rent recorded' },
  home_year_empty_body: {
    fr: 'Aucun mois de {year} n’a encore ete ouvert. Ouvrez un mois pour commencer.',
    en: 'No month of {year} has been opened yet. Open a month to begin.',
  },
  home_months_open: { fr: '{n} mois ouverts', en: '{n} months opened' },
  home_revenue_detail: {
    fr: 'Loyers encaisses, moins les charges mensuelles, moins les depenses approuvees. Calcule mois par mois. Appuyez pour le detail des douze mois.',
    en: 'Rent collected, less monthly charges, less approved expenses. Computed month by month. Tap for the twelve-month detail.',
  },
  home_no_charges: {
    fr: 'Charges mensuelles non configurees',
    en: 'Monthly charges not configured',
  },
  home_no_charges_body: {
    fr: 'Les honoraires de gerance, le versement a la mere et le service de menage ne sont pas encore enregistres : le revenu affiche ne les deduit donc pas. Executez db/007.',
    en: 'The management fee, the payment to the mother and the cleaning service are not recorded yet: the revenue shown does not deduct them. Run db/007.',
  },

  deposit_carried_over: {
    fr: 'Recue avant cette annee, toujours detenue',
    en: 'Received before this year, still held',
  },

  // -------------------------------------------------------------------
  // Actions communes
  // -------------------------------------------------------------------
  save: { fr: 'Enregistrer', en: 'Save' },
  saving: { fr: 'Enregistrement...', en: 'Saving...' },
  cancel: { fr: 'Annuler', en: 'Cancel' },
  add: { fr: 'Ajouter', en: 'Add' },
  edit: { fr: 'Modifier', en: 'Edit' },
  back: { fr: 'Retour', en: 'Back' },
  previous: { fr: 'Precedent', en: 'Previous' },
  next: { fr: 'Suivant', en: 'Next' },
  optional: { fr: 'facultatif', en: 'optional' },
  save_failed: {
    fr: 'L’enregistrement a echoue. Rien n’est perdu : ce que vous avez tape est toujours a l’ecran. Verifiez la connexion et reessayez.',
    en: 'Saving failed. Nothing is lost: what you typed is still on screen. Check the connection and try again.',
  },

  // -------------------------------------------------------------------
  // Suppressions
  // -------------------------------------------------------------------
  delete: { fr: 'Supprimer', en: 'Delete' },
  deleting: { fr: 'Suppression...', en: 'Deleting...' },
  delete_confirm: { fr: 'Confirmer la suppression', en: 'Confirm deletion' },
  delete_cancel: { fr: 'Ne pas supprimer', en: 'Do not delete' },
  delete_failed: {
    fr: 'La suppression a echoue.',
    en: 'Deletion failed.',
  },
  delete_owner_only: {
    fr: 'Seul le proprietaire peut supprimer.',
    en: 'Only the owner can delete.',
  },

  expense_delete: { fr: 'Supprimer cette depense', en: 'Delete this expense' },
  expense_delete_confirm: {
    fr: 'Supprimer definitivement cette depense de {amount} ? Elle disparaitra de l’application, mais la suppression restera inscrite au journal d’audit.',
    en: 'Permanently delete this {amount} expense? It will disappear from the app, but the deletion stays recorded in the audit log.',
  },
  expense_delete_blocked: {
    fr: 'Une depense approuvee entre dans le reglement du mois : elle ne peut pas etre supprimee. Refusez-la d’abord si elle ne doit pas etre payee.',
    en: 'An approved expense enters the monthly settlement: it cannot be deleted. Refuse it first if it should not be paid.',
  },

  tenant_delete: { fr: 'Supprimer ce locataire', en: 'Delete this tenant' },
  tenant_delete_confirm: {
    fr: 'Supprimer definitivement {name} ? Ce locataire n’a aucun bail : rien d’autre ne disparait avec lui.',
    en: 'Permanently delete {name}? This tenant has no lease: nothing else disappears with them.',
  },
  tenant_delete_blocked: {
    fr: 'Ce locataire a {n} bail ou baux enregistres. Il ne peut pas etre supprime : ces baux sont l’historique de l’unite. Seul un locataire cree par erreur, sans aucun bail, peut etre retire.',
    en: 'This tenant has {n} lease(s) recorded. They cannot be deleted: those leases are the unit’s history. Only a tenant created by mistake, with no lease at all, can be removed.',
  },
  tenant_no_lease: { fr: 'Aucun bail', en: 'No lease' },

  // -------------------------------------------------------------------
  // Contrat signe
  // -------------------------------------------------------------------
  lease_contract_add: { fr: 'Ajouter le contrat signe', en: 'Add the signed lease' },
  lease_contract_replace: { fr: 'Remplacer le contrat', en: 'Replace the contract' },
  lease_contract_manage: {
    fr: 'Pieces du bail',
    en: 'Lease documents',
  },
  lease_contract_title: { fr: 'Contrat de bail signe', en: 'Signed lease contract' },
  lease_contract_hint: {
    fr: 'Photographiez le contrat signe par les deux parties.',
    en: 'Photograph the contract signed by both parties.',
  },
  lease_contract_hint_multi: {
    fr: 'Un bail porte autant de pieces qu’il en a : la page des signatures, les conditions, l’etat des lieux d’entree, une piece d’identite. Ajoutez-les une par une, le champ se remet a zero apres chaque envoi.',
    en: 'A lease holds as many documents as it has: the signature page, the conditions, the entry inventory, an ID document. Add them one by one; the field resets after each upload.',
  },
  lease_contract_none: {
    fr: 'Aucune piece rattachee a ce bail pour l’instant.',
    en: 'No document attached to this lease yet.',
  },
  lease_contract_pages: {
    fr: '{n} piece(s) rattachee(s)',
    en: '{n} document(s) attached',
  },
  lease_contract_page: { fr: 'Piece {n}', en: 'Document {n}' },
  lease_contract_primary: { fr: 'Principale', en: 'Primary' },
  lease_contract_set_primary: {
    fr: 'Designer comme principale',
    en: 'Set as primary',
  },
  lease_contract_add_page: { fr: 'Ajouter une piece', en: 'Add a document' },
  lease_contract_more_after: {
    fr: 'Une seule piece ici. Apres l enregistrement, la liste des pieces du bail s ouvre et vous pourrez ajouter les autres pages une par une.',
    en: 'One document here. After saving, the lease document list opens and you can add the other pages one by one.',
  },
  lease_contract_detach: { fr: 'Retirer du bail', en: 'Remove from lease' },
  lease_contract_missing: { fr: 'Contrat non joint', en: 'No contract attached' },

  // -------------------------------------------------------------------
  // Photos et pieces justificatives
  // -------------------------------------------------------------------
  photo_take: { fr: 'Prendre une photo', en: 'Take a photo' },
  photo_choose: { fr: 'Choisir un fichier', en: 'Choose a file' },
  photo_sending: { fr: 'Envoi...', en: 'Sending...' },
  photo_attached: { fr: 'Piece jointe', en: 'Attached' },
  photo_replace: { fr: 'Remplacer', en: 'Replace' },
  photo_sent: { fr: 'Envoyee, {ko} Ko', en: 'Sent, {ko} KB' },
  photo_failed: { fr: 'L’envoi de la piece a echoue.', en: 'Uploading the file failed.' },

  // -------------------------------------------------------------------
  // Unites
  // -------------------------------------------------------------------
  unit_add: { fr: 'Ajouter une unite', en: 'Add a unit' },
  unit_edit: { fr: 'Modifier l’unite', en: 'Edit unit' },
  unit_name: { fr: 'Nom de l’unite', en: 'Unit name' },
  unit_name_hint: {
    fr: 'Exactement comme sur le tableau papier. Ce nom n’est jamais traduit.',
    en: 'Exactly as on the paper sheet. This name is never translated.',
  },
  unit_kind: { fr: 'Type', en: 'Type' },
  unit_floor: { fr: 'Etage', en: 'Floor' },
  unit_notes: { fr: 'Remarques', en: 'Notes' },
  unit_vacant_since_label: { fr: 'Vide depuis le', en: 'Vacant since' },
  unit_vacancy_reason: { fr: 'Pourquoi l’unite est vide', en: 'Why the unit is vacant' },
  unit_vacancy_hint: {
    fr: 'Une unite vide ne genere aucun loyer attendu.',
    en: 'A vacant unit generates no expected rent.',
  },
  unit_detail: { fr: 'Detail de l’unite', en: 'Unit detail' },
  unit_current_lease: { fr: 'Bail en cours', en: 'Current lease' },
  unit_history: { fr: 'Historique des locataires', en: 'Tenant history' },
  unit_no_lease: { fr: 'Aucun bail enregistre pour cette unite.', en: 'No lease recorded for this unit.' },

  // -------------------------------------------------------------------
  // Locataires
  // -------------------------------------------------------------------
  tenants_title: { fr: 'Les locataires', en: 'Tenants' },
  tenant_add: { fr: 'Ajouter un locataire', en: 'Add a tenant' },
  tenant_edit: { fr: 'Modifier le locataire', en: 'Edit tenant' },
  tenant_name: { fr: 'Nom complet', en: 'Full name' },
  tenant_name_hint: {
    fr: 'Comme sur sa piece d’identite. Jamais traduit.',
    en: 'As on their ID document. Never translated.',
  },
  tenant_phone: { fr: 'Telephone', en: 'Phone' },
  tenant_id_type: { fr: 'Type de piece', en: 'ID type' },
  tenant_id_number: { fr: 'Numero de la piece', en: 'ID number' },
  tenant_notes: { fr: 'Remarques', en: 'Notes' },
  tenants_empty: { fr: 'Aucun locataire enregistre.', en: 'No tenants recorded.' },
  id_type_cni: { fr: 'CNI', en: 'CNI' },
  id_type_passeport: { fr: 'Passeport', en: 'Passport' },
  id_type_autre: { fr: 'Autre', en: 'Other' },
  id_type_none: { fr: 'Non renseigne', en: 'Not recorded' },

  // -------------------------------------------------------------------
  // Baux
  // -------------------------------------------------------------------
  lease_add: { fr: 'Nouveau bail', en: 'New lease' },
  lease_tenant: { fr: 'Locataire', en: 'Tenant' },
  lease_unit: { fr: 'Unite', en: 'Unit' },
  lease_rent_ttc: { fr: 'Loyer mensuel', en: 'Monthly rent' },
  lease_rent_ttc_hint: {
    fr: 'Le montant que le locataire paie chaque mois, TVA comprise pour un magasin.',
    en: 'The amount the tenant pays each month, VAT included for a shop.',
  },
  lease_tva: { fr: 'TVA', en: 'VAT' },
  lease_tva_hint: {
    fr: 'La TVA de 18 % s’applique aux magasins et est deja comprise dans le loyer. A confirmer avec votre comptable.',
    en: 'The 18% VAT applies to shops and is already included in the rent. To be confirmed with your accountant.',
  },
  lease_tva_none: { fr: 'Pas de TVA', en: 'No VAT' },
  lease_tva_18: { fr: 'TVA 18 % comprise', en: 'VAT 18% included' },
  lease_deposit: { fr: 'Caution', en: 'Security deposit' },
  lease_advance: { fr: 'Mois d’avance', en: 'Advance month' },
  lease_agency_fee: { fr: 'Frais d’agence', en: 'Agency fee' },
  lease_start: { fr: 'Debut du bail', en: 'Lease start' },
  lease_end: { fr: 'Fin du bail', en: 'Lease end' },
  lease_status: { fr: 'Statut', en: 'Status' },
  lease_status_actif: { fr: 'En cours', en: 'Active' },
  lease_status_expire: { fr: 'Expire', en: 'Expired' },
  lease_status_termine: { fr: 'Termine', en: 'Ended' },
  lease_contract: { fr: 'Photo du contrat', en: 'Contract photo' },
  lease_change_rent: { fr: 'Changer le loyer', en: 'Change the rent' },
  lease_change_reason: { fr: 'Motif du changement', en: 'Reason for the change' },
  lease_change_reason_hint: {
    fr: 'Obligatoire. Un changement de loyer cree un nouveau bail et ne modifie jamais l’ancien : c’est ainsi que l’historique reste verifiable.',
    en: 'Required. A rent change creates a new lease and never edits the old one: that is how the history stays checkable.',
  },
  lease_ht: { fr: 'Dont hors taxe', en: 'Of which excluding tax' },
  lease_tva_amount: { fr: 'Dont TVA', en: 'Of which VAT' },

  // -------------------------------------------------------------------
  // Baux a renouveler
  // -------------------------------------------------------------------
  leases_expiring_title: { fr: 'Baux a renouveler', en: 'Leases to renew' },
  leases_expired_banner: {
    fr: '{n} bail expire',
    en: '{n} expired lease',
  },
  leases_expired_banner_plural: {
    fr: '{n} baux expires',
    en: '{n} expired leases',
  },
  leases_expiring_soon: {
    fr: '{n} bail expire dans moins de 60 jours',
    en: '{n} lease expires within 60 days',
  },
  leases_expiring_soon_plural: {
    fr: '{n} baux expirent dans moins de 60 jours',
    en: '{n} leases expire within 60 days',
  },
  lease_expired_since: { fr: 'Expire depuis {n} jours', en: 'Expired {n} days ago' },
  lease_expires_in: { fr: 'Expire dans {n} jours', en: 'Expires in {n} days' },
  leases_expiring_none: { fr: 'Aucun bail a renouveler.', en: 'No lease needs renewing.' },

  // -------------------------------------------------------------------
  // Loyers
  // -------------------------------------------------------------------
  rent_month: { fr: 'Loyers du mois', en: 'Rent for the month' },
  rent_expected: { fr: 'Attendu', en: 'Expected' },
  rent_received: { fr: 'Recu', en: 'Received' },
  rent_balance: { fr: 'Reste a payer', en: 'Still owed' },
  rent_status_paye: { fr: 'Paye', en: 'Paid' },
  rent_status_partiel: { fr: 'Partiel', en: 'Partial' },
  rent_status_impaye: { fr: 'Impaye', en: 'Unpaid' },
  rent_covered_advance: { fr: 'Couvert par le mois d’avance', en: 'Covered by advance month' },
  rent_open_month: { fr: 'Ouvrir le mois', en: 'Open the month' },
  rent_open_month_hint: {
    fr: 'Cree la ligne de loyer attendu pour chaque bail en cours. A faire une fois par mois.',
    en: 'Creates the expected-rent line for each current lease. Once a month.',
  },
  rent_month_not_open: {
    fr: 'Ce mois n’est pas encore ouvert',
    en: 'This month is not open yet',
  },
  rent_month_not_open_body: {
    fr: 'Aucun loyer attendu n’a encore ete cree pour ce mois. Ouvrez le mois pour commencer a enregistrer les versements.',
    en: 'No expected rent has been created for this month yet. Open the month to start recording payments.',
  },
  rent_opened: { fr: '{n} loyers attendus crees.', en: '{n} expected rents created.' },
  rent_due_on: { fr: 'Du le {date}', en: 'Due {date}' },
  rent_total_expected: { fr: 'Total attendu', en: 'Total expected' },
  rent_total_received: { fr: 'Total encaisse', en: 'Total collected' },

  // Versements
  payment_add: { fr: 'Enregistrer un versement', en: 'Record a payment' },
  payment_amount: { fr: 'Montant recu', en: 'Amount received' },
  payment_date: { fr: 'Date du versement', en: 'Payment date' },
  payment_method: { fr: 'Mode de paiement', en: 'Payment method' },
  payment_method_especes: { fr: 'Especes', en: 'Cash' },
  payment_method_wave: { fr: 'Wave', en: 'Wave' },
  payment_method_virement: { fr: 'Virement', en: 'Bank transfer' },
  payment_method_cheque: { fr: 'Cheque', en: 'Cheque' },
  payment_receipt_number: { fr: 'Numero de recu', en: 'Receipt number' },
  payment_receipt_photo: { fr: 'Photo du recu', en: 'Receipt photo' },
  payment_proof_at_bank: {
    fr: 'Aucune photo a joindre ici. Les loyers etant encaisses puis verses en une seule fois par mois, la piece du mois est le bordereau de versement : il se joint dans l’ecran Banque.',
    en: 'No photo to attach here. Rent is collected then deposited once a month, so the month’s document is the bank deposit slip: attach it in the Bank screen.',
  },

  // -------------------------------------------------------------------
  // Reglement du mois : la cascade
  // -------------------------------------------------------------------
  settlement_title: { fr: 'Reglement du mois', en: 'Monthly settlement' },
  settlement_intro: {
    fr: 'La cascade du mois : loyers encaisses, deductions, net a verser.',
    en: 'The month’s waterfall: rent collected, deductions, net to deposit.',
  },
  settlement_rent_collected: {
    fr: 'Loyers encaisses',
    en: 'Rent collected',
  },
  settlement_pct_detail: {
    fr: '{rate} % de {base}',
    en: '{rate}% of {base}',
  },
  settlement_payee: { fr: 'Verse a', en: 'Paid to' },
  settlement_receipt_required: {
    fr: 'Recu exige avant cloture',
    en: 'Receipt required before closing',
  },
  settlement_expenses: { fr: 'Depenses approuvees', en: 'Approved expenses' },
  settlement_expenses_detail: {
    fr: 'Les depenses approuvees uniquement. Celles en attente n’entrent pas dans le releve.',
    en: 'Approved expenses only. Pending ones do not enter the statement.',
  },
  settlement_net: { fr: 'Net a verser', en: 'Net to deposit' },
  settlement_rule: {
    fr: 'Rien d’autre ne reduit ce net. Toute autre somme doit passer par une depense que vous approuvez, ou par une charge mensuelle creee explicitement.',
    en: 'Nothing else reduces this net. Any other amount must go through an expense you approve, or a monthly charge created explicitly.',
  },
  settlement_split: { fr: 'Repartition entre les comptes', en: 'Split across accounts' },
  settlement_split_detail: {
    fr: 'Si le net est inferieur au montant fixe, le compte fixe prend tout et l’autre recoit zero.',
    en: 'If the net is below the fixed amount, the fixed account takes all and the other gets zero.',
  },
  settlement_no_split: {
    fr: 'Aucune regle de repartition configuree',
    en: 'No split rule configured',
  },
  settlement_no_split_body: {
    fr: 'Les dates d’effet de la regle 600 000 sur BHS et le reste sur SGBS ne m’ont jamais ete communiquees. Renseignez-les dans db/seed.sql pour voir la repartition ici.',
    en: 'The effective dates of the 600 000 to BHS and remainder to SGBS rule were never given to me. Fill them in db/seed.sql to see the split here.',
  },
  settlement_negative: { fr: 'Le net est negatif ce mois', en: 'The net is negative this month' },
  settlement_negative_body: {
    fr: 'Les deductions et les depenses depassent les loyers encaisses. Le montant n’est pas ramene a zero : ce serait masquer le fait que le mois coute plus qu’il ne rapporte.',
    en: 'Deductions and expenses exceed the rent collected. The figure is not floored at zero: that would hide the fact that the month costs more than it brings in.',
  },

  // -------------------------------------------------------------------
  // Charges mensuelles recurrentes
  // -------------------------------------------------------------------
  recurring_title: { fr: 'Charges mensuelles', en: 'Monthly charges' },
  recurring_intro: {
    fr: 'Les sommes deduites des loyers chaque mois. Un changement de taux cree une nouvelle ligne datee : les mois deja calcules gardent l’ancien taux.',
    en: 'Amounts deducted from rent each month. Changing a rate creates a new dated line: months already calculated keep the old rate.',
  },
  recurring_empty: {
    fr: 'Aucune charge mensuelle configuree. Executez db/007.',
    en: 'No monthly charge configured. Run db/007.',
  },
  recurring_from: { fr: 'Depuis le {date}', en: 'Since {date}' },
  recurring_until: { fr: 'jusqu’au {date}', en: 'until {date}' },
  recurring_percent_of: { fr: '{rate} % des loyers encaisses', en: '{rate}% of rent collected' },
  recurring_owner_only: {
    fr: 'Seul le proprietaire peut modifier un taux ou un montant.',
    en: 'Only the owner can change a rate or an amount.',
  },

  // -------------------------------------------------------------------
  // Versements en banque
  // -------------------------------------------------------------------
  bank_title: { fr: 'Versements en banque', en: 'Bank deposits' },
  bank_intro: {
    fr: 'Un versement par mois, avec son bordereau. C’est la piece justificative du mois.',
    en: 'One deposit a month, with its slip. That is the month’s supporting document.',
  },
  bank_add: { fr: 'Nouveau versement', en: 'New deposit' },
  bank_edit: { fr: 'Modifier le versement', en: 'Edit deposit' },
  bank_edit_hint: {
    fr: 'Touchez un versement pour le modifier.',
    en: 'Tap a deposit to edit it.',
  },
  bank_delete: { fr: 'Supprimer ce versement', en: 'Delete this deposit' },
  bank_delete_confirm: {
    fr: 'Supprimer definitivement ce versement de {amount} ? La photo du bordereau reste consultable dans Documents, et la suppression reste inscrite au journal d’audit.',
    en: 'Permanently delete this {amount} deposit? The slip photo stays available in Documents, and the deletion stays recorded in the audit log.',
  },
  period_closed: { fr: 'Mois cloture', en: 'Month closed' },
  bank_locked_closed: {
    fr: 'Le mois {period} est cloture : ce versement ne peut etre ni modifie ni supprime avant sa reouverture par le proprietaire.',
    en: 'Month {period} is closed: this deposit cannot be edited or deleted until the owner reopens it.',
  },
  bank_empty: { fr: 'Aucun versement enregistre', en: 'No deposit recorded' },
  bank_empty_body: {
    fr: 'Aucun versement en banque n’a encore ete saisi pour cette annee.',
    en: 'No bank deposit has been recorded for this year yet.',
  },
  bank_account: { fr: 'Banque', en: 'Bank' },
  bank_deposited_on: { fr: 'Date du versement', en: 'Deposit date' },
  bank_deposited: { fr: 'Montant depose', en: 'Amount deposited' },
  bank_deposited_hint: {
    fr: 'Le montant remis au guichet.',
    en: 'The amount handed over at the counter.',
  },
  bank_credited: { fr: 'Montant credite', en: 'Amount credited' },
  bank_credited_hint: {
    fr: 'Le montant hors frais, tel qu’il apparait sur le compte. C’est le releve de la banque qui fait foi, pas le calcul.',
    en: 'The amount net of charges, as it appears on the account. The bank statement is what counts, not the calculation.',
  },
  bank_credited_expected: { fr: 'Credit attendu', en: 'Expected credit' },
  bank_stamp_duty: { fr: 'Droit de timbre', en: 'Stamp duty' },
  bank_stamp_duty_hint: {
    fr: 'Observe a 200 F CFA par bordereau. Reste une ligne a part, jamais fondue dans les honoraires de gerance.',
    en: 'Observed at 200 F CFA per slip. Stays a separate line, never merged into the management fee.',
  },
  bank_reference: { fr: 'Reference du bordereau', en: 'Slip reference' },
  bank_remitter: { fr: 'Depose par', en: 'Deposited by' },
  bank_remitter_hint: {
    fr: 'Qui a physiquement remis l’argent au guichet.',
    en: 'Who physically handed the money over at the counter.',
  },
  bank_slip: { fr: 'Bordereau', en: 'Deposit slip' },
  bank_slip_required: { fr: 'Le bordereau est obligatoire', en: 'The slip is required' },
  bank_slip_required_body: {
    fr: 'Un versement en banque ne peut pas exister sans son bordereau : la base elle-meme le refuse. Prenez la photo pour pouvoir enregistrer.',
    en: 'A bank deposit cannot exist without its slip: the database itself refuses it. Take the photo to be able to save.',
  },
  bank_total_deposited: { fr: 'Total depose', en: 'Total deposited' },
  bank_total_credited: { fr: 'Total credite', en: 'Total credited' },
  bank_total_stamp: { fr: 'Total des droits de timbre', en: 'Total stamp duty' },
  bank_gap: { fr: 'Ecart du mois', en: 'Gap for the month' },
  bank_gap_hint: {
    fr: 'Difference entre les loyers encaisses et ce qui a ete credite en banque. Un ecart n’est pas forcement une erreur : honoraires, versement a la mere et depenses approuvees se deduisent avant le versement.',
    en: 'Difference between rent collected and what the bank credited. A gap is not necessarily an error: the fee, the payment to the mother and approved expenses are deducted before the deposit.',
  },
  payment_notes: { fr: 'Remarques', en: 'Notes' },
  payments_none: { fr: 'Aucun versement enregistre.', en: 'No payment recorded.' },
  payments_list: { fr: 'Versements', en: 'Payments' },
  payment_late_by: { fr: 'En retard de {n} jours', en: '{n} days late' },
  payment_on_time: { fr: 'Dans les delais', en: 'On time' },

  // Penalite de retard
  penalty_title: { fr: 'Penalite de retard suggeree', en: 'Suggested late penalty' },
  penalty_detail: {
    fr: '{days} jours × {rate} % par jour sur {base}',
    en: '{days} days × {rate}% per day on {base}',
  },
  penalty_unconfirmed: {
    fr: 'A CONFIRMER : le taux de 5 % par jour vient de l’Article 3, mais la base retenue ici est le solde restant du, le calcul est simple et sans plafond. Rien n’est applique sans votre accord.',
    en: 'TO CONFIRM: the 5% per day rate comes from Article 3, but the base used here is the outstanding balance, calculated simply and without a cap. Nothing is applied without your approval.',
  },
  penalty_apply: { fr: 'Appliquer la penalite', en: 'Apply the penalty' },
  penalty_waive: { fr: 'Ne pas appliquer', en: 'Do not apply' },
  penalty_applied: { fr: 'Penalite appliquee : {amount}', en: 'Penalty applied: {amount}' },
  penalty_waived: { fr: 'Penalite annulee', en: 'Penalty waived' },
  penalty_reason: { fr: 'Motif de la decision', en: 'Reason for the decision' },
  penalty_owner_only: {
    fr: 'Seul le proprietaire peut appliquer ou annuler une penalite.',
    en: 'Only the owner can apply or waive a penalty.',
  },

  // Arrieres
  arrears_title: { fr: 'Arrieres par locataire', en: 'Arrears by tenant' },
  arrears_none: { fr: 'Aucun arriere.', en: 'No arrears.' },
  arrears_months: { fr: '{n} mois', en: '{n} months' },
  arrears_since: { fr: 'Depuis {period}', en: 'Since {period}' },

  // -------------------------------------------------------------------
  // Depenses
  // -------------------------------------------------------------------
  expenses_month: { fr: 'Depenses du mois', en: 'Expenses for the month' },
  expense_add: { fr: 'Nouvelle depense', en: 'New expense' },
  expense_date: { fr: 'Date de la depense', en: 'Expense date' },
  expense_category: { fr: 'Categorie', en: 'Category' },
  expense_unit: { fr: 'Unite concernee', en: 'Unit concerned' },
  expense_whole_building: { fr: 'Tout l’immeuble', en: 'Whole building' },
  expense_supplier: { fr: 'Fournisseur', en: 'Supplier' },
  expense_amount: { fr: 'Montant', en: 'Amount' },
  expense_paid_by: { fr: 'Paye par', en: 'Paid by' },
  expense_description: { fr: 'Description des travaux', en: 'Description of the work' },
  expense_invoice: { fr: 'Photo de la facture', en: 'Invoice photo' },
  expense_no_invoice_warning: {
    fr: 'Sans facture, la depense reste en attente et n’entre pas dans le releve du mois.',
    en: 'Without an invoice, the expense stays pending and does not enter the monthly statement.',
  },
  expense_invoice_required: {
    fr: 'La piece justificative est obligatoire',
    en: 'A supporting document is required',
  },
  expense_invoice_required_body: {
    fr: 'Une depense ne peut pas etre enregistree sans sa piece. S’il n’y a pas de facture, photographiez ce qui en tient lieu : un recu manuscrit, un bon, ou le travail effectue. Une photo, meme imparfaite, vaut mieux qu’une depense non enregistree.',
    en: 'An expense cannot be recorded without its document. If there is no invoice, photograph whatever stands in for one: a handwritten receipt, a note, or the work done. A photo, even an imperfect one, beats an unrecorded expense.',
  },
  expenses_none: { fr: 'Aucune depense ce mois.', en: 'No expense this month.' },
  expense_status_en_attente: { fr: 'En attente', en: 'Pending' },
  expense_status_approuve: { fr: 'Approuve', en: 'Approved' },
  expense_status_refuse: { fr: 'Refuse', en: 'Refused' },
  expense_total_approved: { fr: 'Total approuve', en: 'Total approved' },
  expense_total_pending: { fr: 'Total en attente', en: 'Total pending' },

  cat_plomberie: { fr: 'Plomberie', en: 'Plumbing' },
  cat_vidange: { fr: 'Vidange', en: 'Septic emptying' },
  cat_eau: { fr: 'Eau', en: 'Water' },
  cat_electricite: { fr: 'Electricite', en: 'Electricity' },
  cat_peinture: { fr: 'Peinture', en: 'Painting' },
  cat_amende: { fr: 'Amende', en: 'Fine' },
  cat_menage: { fr: 'Menage', en: 'Cleaning' },
  cat_autre: { fr: 'Autre', en: 'Other' },

  payer_proprietaire: { fr: 'Le proprietaire', en: 'The owner' },
  payer_gerant: { fr: 'Le gerant', en: 'The manager' },
  payer_assistant: { fr: 'L’assistante', en: 'The assistant' },
  payer_mere: { fr: 'La mere du proprietaire', en: 'The owner’s mother' },
  payer_locataire: { fr: 'Le locataire', en: 'The tenant' },
  payer_autre: { fr: 'Autre', en: 'Other' },

  // Article 3
  tenant_charge_flag: { fr: 'A la charge du locataire', en: 'Tenant’s responsibility' },
  tenant_charge_detected: {
    fr: 'Cela ressemble a une reparation a la charge du locataire selon l’Article 3 (WC, robinets, ampoules, serrures, vidange de fosse, douche). Elle n’est pas portee a votre charge par defaut.',
    en: 'This looks like a repair that is the tenant’s responsibility under Article 3 (WC, taps, bulbs, locks, septic emptying, shower). It is not charged to you by default.',
  },

  // Doublons
  duplicate_warning_title: { fr: 'Doublon possible', en: 'Possible duplicate' },
  duplicate_warning_body: {
    fr: 'Une depense tres proche existe deja : meme categorie, meme unite, moins de 30 jours d’ecart et montant voisin. Verifiez que ces travaux n’ont pas deja ete payes autrement.',
    en: 'A very similar expense already exists: same category, same unit, less than 30 days apart, close amount. Check that this work has not already been paid another way.',
  },

  // Approbation
  approvals_title: { fr: 'A approuver', en: 'To approve' },
  approvals_none: { fr: 'Aucune depense n’attend votre decision.', en: 'No expense awaits your decision.' },
  approve: { fr: 'Approuver', en: 'Approve' },
  reject: { fr: 'Refuser', en: 'Refuse' },
  reject_reason: { fr: 'Motif du refus', en: 'Reason for refusal' },
  reject_reason_hint: { fr: 'Obligatoire.', en: 'Required.' },
  approve_needs_invoice: {
    fr: 'Impossible d’approuver sans facture jointe. La base l’interdit.',
    en: 'Cannot approve without an attached invoice. The database forbids it.',
  },

  // -------------------------------------------------------------------
  // Cautions
  // -------------------------------------------------------------------
  deposits_title: { fr: 'Cautions', en: 'Deposits' },
  deposit_add: { fr: 'Enregistrer une caution', en: 'Record a deposit' },
  deposit_edit: { fr: 'Modifier la caution', en: 'Edit deposit' },
  deposit_edit_hint: {
    fr: 'Touchez une caution pour la modifier.',
    en: 'Tap a deposit to edit it.',
  },
  deposit_delete: { fr: 'Supprimer cette caution', en: 'Delete this deposit' },
  deposit_delete_confirm: {
    fr: 'Supprimer definitivement cette caution de {amount} ? Elle disparaitra de l’application, mais la suppression restera inscrite au journal d’audit.',
    en: 'Permanently delete this {amount} deposit? It will disappear from the app, but the deletion stays recorded in the audit log.',
  },
  deposit_delete_blocked_movements: {
    fr: 'Cette caution porte {n} mouvement(s) d’entree ou de sortie. Elle ne peut pas etre supprimee : ces mouvements sont la trace de l’argent. Seule une caution saisie par erreur, sans aucun mouvement, peut etre retiree.',
    en: 'This deposit has {n} in or out movement(s). It cannot be deleted: those movements are the record of the money. Only a deposit entered by mistake, with no movement at all, can be removed.',
  },
  deposit_will_lock: {
    fr: 'Attention : une fois enregistree comme soldee, cette caution ne pourra plus etre ni modifiee ni supprimee depuis l’application. Verifiez le montant et la piece avant d’enregistrer.',
    en: 'Careful: once recorded as settled, this deposit can no longer be edited or deleted from the app. Check the amount and the proof before saving.',
  },
  deposit_locked_settled: {
    fr: 'Cette caution est deja soldee : l’argent est sorti, et cette sortie a exige un approbateur, une piece et l’etat des lieux de sortie. Elle ne peut plus etre ni modifiee ni supprimee.',
    en: 'This deposit is already settled: the money has left, and that exit required an approver, a proof and the exit inventory. It can no longer be edited or deleted.',
  },
  deposit_movements_warning: {
    fr: '{n} mouvement(s) sont enregistres sur cette caution. Si vous corrigez le montant, verifiez que les mouvements restent coherents.',
    en: '{n} movement(s) are recorded on this deposit. If you correct the amount, check that the movements stay consistent.',
  },
  deposit_kind: { fr: 'Nature', en: 'Kind' },
  deposit_kind_caution: { fr: 'Caution', en: 'Security deposit' },
  deposit_kind_mois_avance: { fr: 'Mois d’avance', en: 'Advance month' },
  deposit_kind_frais_agence: { fr: 'Frais d’agence', en: 'Agency fee' },
  deposit_amount: { fr: 'Montant', en: 'Amount' },
  deposit_received_on: { fr: 'Recu le', en: 'Received on' },
  deposit_held_by: { fr: 'Detenu par', en: 'Held by' },
  deposit_held_by_note: { fr: 'Precisez', en: 'Specify' },
  deposit_proof: { fr: 'Preuve du versement', en: 'Proof of payment' },
  deposit_status: { fr: 'Etat', en: 'Status' },
  deposit_status_detenu: { fr: 'Detenue', en: 'Held' },
  deposit_status_rembourse: { fr: 'Remboursee', en: 'Refunded' },
  deposit_status_applique: { fr: 'Appliquee', en: 'Applied' },
  deposit_status_introuvable: { fr: 'Introuvable', en: 'Missing' },
  deposits_none: { fr: 'Aucune caution enregistree.', en: 'No deposit recorded.' },
  deposits_total_held: { fr: 'Total des cautions detenues', en: 'Total deposits held' },
  deposits_by_holder: { fr: 'Par detenteur', en: 'By holder' },
  deposit_no_proof: { fr: 'Sans preuve', en: 'No proof' },

  holder_proprietaire: { fr: 'Le proprietaire', en: 'The owner' },
  holder_gerant: { fr: 'Le gerant', en: 'The manager' },
  holder_mere: { fr: 'La mere du proprietaire', en: 'The owner’s mother' },
  holder_banque: { fr: 'La banque', en: 'The bank' },
  holder_autre: { fr: 'Autre', en: 'Other' },

  // Remboursement
  refund_title: { fr: 'Rembourser une caution', en: 'Refund a deposit' },
  refund_checklist: { fr: 'A verifier avant tout remboursement', en: 'Required before any refund' },
  refund_exit_inventory: { fr: 'Etat des lieux de sortie', en: 'Exit inventory' },
  refund_utilities: { fr: 'Eau et electricite regles', en: 'Water and electricity settled' },
  refund_owner_approval: { fr: 'Accord ecrit du proprietaire', en: 'Owner’s written approval' },
  refund_amount: { fr: 'Montant rembourse', en: 'Amount refunded' },
  refund_date: { fr: 'Date du remboursement', en: 'Refund date' },
  refund_proof: { fr: 'Preuve du remboursement', en: 'Proof of refund' },
  refund_source: { fr: 'Origine de l’argent', en: 'Where the money comes from' },
  refund_source_caution_origine: { fr: 'La caution d’origine', en: 'The original deposit' },
  refund_source_loyers: { fr: 'Les loyers', en: 'The rent' },
  refund_source_autre_caution: { fr: 'La caution d’un autre locataire', en: 'Another tenant’s deposit' },
  refund_source_proprietaire: { fr: 'Le proprietaire', en: 'The owner' },
  refund_blocked: {
    fr: 'Impossible d’enregistrer ce remboursement. Une caution ne peut jamais etre remboursee avec les loyers ni avec la caution d’un autre locataire. L’argent doit venir de la caution d’origine. Contactez le proprietaire.',
    en: 'This refund cannot be recorded. A deposit can never be refunded out of rent or out of another tenant’s deposit. The money must come from the original deposit. Contact the owner.',
  },
  refund_owner_override: {
    fr: 'Vous enregistrez une origine anormale. Cette ligne sera signalee en rouge de facon permanente.',
    en: 'You are recording an abnormal source. This line will be flagged in red permanently.',
  },

  // -------------------------------------------------------------------
  // Pieces justificatives
  // -------------------------------------------------------------------
  documents_title: { fr: 'Pieces justificatives', en: 'Supporting documents' },
  documents_empty: {
    fr: 'Aucune piece ne correspond a ces filtres.',
    en: 'No document matches these filters.',
  },
  doc_view: { fr: 'Voir', en: 'View' },
  doc_open: { fr: 'Ouvrir', en: 'Open' },
  doc_share: { fr: 'Partager', en: 'Share' },
  doc_kind: { fr: 'Type de piece', en: 'Document type' },
  doc_uploaded_at: { fr: 'Envoyee le', en: 'Uploaded on' },
  doc_size: { fr: 'Taille', en: 'Size' },
  doc_url_failed: {
    fr: 'Impossible d’obtenir un acces a ce fichier. Verifiez la connexion.',
    en: 'Could not get access to this file. Check the connection.',
  },
  doc_pdf_hint: {
    fr: 'Ce document est un PDF. Appuyez sur Ouvrir pour l’afficher.',
    en: 'This document is a PDF. Press Open to display it.',
  },
  doc_no_preview: {
    fr: 'Ce type de fichier ne s’affiche pas ici. Appuyez sur Ouvrir.',
    en: 'This file type cannot be shown here. Press Open.',
  },
  doc_kind_contrat: { fr: 'Contrat', en: 'Contract' },
  doc_kind_quittance: { fr: 'Quittance', en: 'Rent receipt' },
  doc_kind_recu: { fr: 'Recu', en: 'Receipt' },
  doc_kind_facture: { fr: 'Facture', en: 'Invoice' },
  doc_kind_bordereau: { fr: 'Bordereau de versement', en: 'Bank deposit slip' },
  doc_kind_etat_des_lieux: { fr: 'Etat des lieux', en: 'Inventory' },
  doc_kind_piece_identite: { fr: 'Piece d’identite', en: 'ID document' },
  doc_kind_photo: { fr: 'Photo', en: 'Photo' },
  doc_kind_autre: { fr: 'Autre', en: 'Other' },
  filter_all: { fr: 'Tous', en: 'All' },

  // -------------------------------------------------------------------
  // Rapports
  // -------------------------------------------------------------------
  reports_title: { fr: 'Rapports', en: 'Reports' },
  reports_intro: {
    fr: 'Chaque rapport s’exporte vers Excel et s’imprime en PDF.',
    en: 'Every report exports to Excel and prints to PDF.',
  },
  report_monthly: { fr: 'Par mois', en: 'By month' },
  report_month_or_year: { fr: 'Par mois ou par annee', en: 'By month or by year' },
  report_scope_month: { fr: 'Un mois', en: 'One month' },
  report_scope_year: { fr: 'Une annee', en: 'One year' },
  report_subtotal: { fr: 'Sous-total du mois', en: 'Month subtotal' },
  report_total_year: { fr: 'Total {year}', en: 'Total {year}' },
  report_months_covered: { fr: '{n} mois', en: '{n} months' },
  report_year_note: {
    fr: 'Annee {year} : les douze periodes de {year}, chacune courant du 5 au 5. Le total annuel va donc du 05/01/{year} au 05/01 de l’annee suivante.',
    en: 'Year {year}: the twelve periods of {year}, each running from the 5th to the 5th. The annual total therefore runs from 05/01/{year} to 05/01 of the following year.',
  },
  report_all_time: { fr: 'Toutes periodes', en: 'All periods' },
  report_empty: { fr: 'Aucune donnee pour ce rapport.', en: 'No data for this report.' },
  report_rows: { fr: '{n} lignes', en: '{n} rows' },
  report_total: { fr: 'Total', en: 'Total' },
  report_export_excel: { fr: 'Exporter vers Excel', en: 'Export to Excel' },
  report_export_pdf: { fr: 'Imprimer en PDF', en: 'Print to PDF' },
  report_period: { fr: 'Mois', en: 'Month' },
  report_days_late: { fr: 'Jours de retard', en: 'Days late' },
  report_months_owing: { fr: 'Mois dus', en: 'Months owing' },
  report_oldest_unpaid: { fr: 'Plus ancien impaye', en: 'Oldest unpaid' },
  report_count: { fr: 'Nombre', en: 'Count' },
  report_annual: { fr: 'Sur douze mois', en: 'Over twelve months' },
  report_years_occupied: { fr: 'Annees d occupation', en: 'Years occupied' },
  report_occupancy: { fr: 'Duree d occupation', en: 'Occupancy' },

  report_revenue: { fr: 'Revenu de chaque mois', en: 'Revenue for each month' },
  report_revenue_desc: {
    fr: 'Les douze mois de l’annee : loyers encaisses, charges mensuelles deduites, depenses, et ce qui reste.',
    en: 'The twelve months of the year: rent collected, monthly charges deducted, expenses, and what remains.',
  },
  report_revenue_note: {
    fr: 'Les depenses en attente sont affichees mais NON deduites : seules les depenses approuvees reduisent le revenu. Un mois sans loyer enregistre apparait a zero, ce qui n’est pas la meme chose qu’un mois sans revenu.',
    en: 'Pending expenses are shown but NOT deducted: only approved expenses reduce revenue. A month with no rent recorded shows as zero, which is not the same as a month with no revenue.',
  },
  report_charges_total: { fr: 'Total des charges', en: 'Total charges' },
  report_net_revenue: { fr: 'Revenu net', en: 'Net revenue' },

  report_rent_status: { fr: 'Etat des loyers du mois', en: 'Rent status for the month' },
  report_rent_status_desc: {
    fr: 'Attendu, recu et reste a payer, unite par unite.',
    en: 'Expected, received and still owed, unit by unit.',
  },
  report_rent_payments: { fr: 'Versements de loyer', en: 'Rent payments' },
  report_rent_payments_desc: {
    fr: 'Chaque versement du mois, avec son mode, son recu et son retard.',
    en: 'Every payment of the month, with method, receipt and lateness.',
  },
  report_arrears: { fr: 'Arrieres par locataire', en: 'Arrears by tenant' },
  report_arrears_desc: {
    fr: 'Tout ce qui reste du, tous mois confondus.',
    en: 'Everything still owed, across all months.',
  },
  report_tenants: { fr: 'Locataires et baux', en: 'Tenants and leases' },
  report_tenants_desc: {
    fr: 'Qui occupe quoi, a quel loyer, jusqu’a quelle date.',
    en: 'Who occupies what, at what rent, until when.',
  },
  report_rent_roll: { fr: 'Loyer theorique de l’immeuble', en: 'Building rent roll' },
  report_rent_roll_desc: {
    fr: 'Ce que produit un mois plein, hors taxe, TVA et TTC.',
    en: 'What a full month produces, excluding tax, VAT and inclusive.',
  },
  report_rent_roll_note: {
    fr: 'Loyer theorique : ce que l’immeuble produirait si tout etait paye. Il ne tient compte ni des impayes ni des vacances.',
    en: 'Theoretical rent: what the building would produce if everything were paid. It ignores arrears and vacancies.',
  },
  report_deposits: { fr: 'Cautions', en: 'Deposits' },
  report_deposits_desc: {
    fr: 'Montant, detenteur, etat et preuve de chaque caution.',
    en: 'Amount, holder, status and proof for each deposit.',
  },
  report_expenses: { fr: 'Depenses du mois', en: 'Expenses for the month' },
  report_expenses_desc: {
    fr: 'Chaque depense, son etat et sa facture.',
    en: 'Every expense, its status and its invoice.',
  },
  report_expenses_category: { fr: 'Depenses par categorie', en: 'Expenses by category' },
  report_expenses_category_desc: {
    fr: 'Synthese du mois, approuve et en attente separes.',
    en: 'Monthly summary, approved and pending kept apart.',
  },
  report_documents: { fr: 'Inventaire des pieces', en: 'Document inventory' },
  report_documents_desc: {
    fr: 'Toutes les pieces envoyees, par type et par mois.',
    en: 'Every uploaded document, by type and month.',
  },

  // -------------------------------------------------------------------
  // Configuration manquante
  // -------------------------------------------------------------------
  config_missing_title: {
    fr: 'Configuration incomplete',
    en: 'Configuration incomplete',
  },
  config_missing_body: {
    fr: 'L’adresse du projet Supabase ou la cle publique n’est pas renseignee. L’application ne peut pas se connecter a la base.',
    en: 'The Supabase project URL or public key is missing. The app cannot reach the database.',
  },
} as const

export type StringKey = keyof typeof strings

/**
 * Traduit une cle. Les valeurs entre accolades dans le texte, comme
 * {email} ou {n}, sont remplacees par ce que vous passez en parametre.
 */
export function translate(
  key: StringKey,
  lang: Lang,
  vars?: Record<string, string | number>,
): string {
  let text: string = strings[key][lang]
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      text = text.split(`{${name}}`).join(String(value))
    }
  }
  return text
}
